import React, {
  useState,
  useEffect,
} from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Event as EventIcon,
  Edit as EditIcon,
  SportsEsports as GameIcon,
  Settings as SettingsIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import gulagService from '../../services/gulag';
import GamePointsConfigDialog from './GamePointsConfigDialog';
import TimeTrialManagement from './TimeTrialManagement';

function TabPanel({ children, value, index }) {
    return (
        <div hidden={value !== index}>
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

function GulagManagement() {
    const [activeTab, setActiveTab] = useState(0);
    const [events, setEvents] = useState([]);
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [editEventDialog, setEditEventDialog] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [eventSettings, setEventSettings] = useState({
        gulag_enabled: false,
        gulag_points_multiplier: 1.0
    });
    const [pointsConfigDialog, setPointsConfigDialog] = useState(false);
    const [selectedGame, setSelectedGame] = useState(null);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        setError(null);

        try {
            // Load all events
            const eventsResult = await gulagService.getAllEventsForAdmin();
            if (eventsResult.success) {
                setEvents(eventsResult.data);
            }

            // Load all games
            const gamesResult = await gulagService.getGames();
            if (gamesResult.success) {
                setGames(gamesResult.data);
            }
        } catch (err) {
            console.error('Error loading Gulag data:', err);
            setError('Failed to load Gulag management data');
        } finally {
            setLoading(false);
        }
    };

    const handleEditEvent = (event) => {
        setSelectedEvent(event);
        setEventSettings({
            gulag_enabled: event.gulag_enabled,
            gulag_points_multiplier: event.gulag_points_multiplier
        });
        setEditEventDialog(true);
    };

    const handleSaveEventSettings = async () => {
        if (!selectedEvent) return;

        const result = await gulagService.updateEventGulagSettings(
            selectedEvent.id,
            eventSettings.gulag_enabled,
            eventSettings.gulag_points_multiplier
        );

        if (result.success) {
            setSuccess('Event Gulag settings updated successfully!');
            setEditEventDialog(false);
            loadData();
        } else {
            setError(result.error);
        }
    };

    const handleToggleEventGulag = async (eventId, currentStatus) => {
        const result = await gulagService.updateEventGulagSettings(
            eventId,
            !currentStatus,
            1.0
        );

        if (result.success) {
            setSuccess(`Gulag ${!currentStatus ? 'enabled' : 'disabled'} for event`);
            loadData();
        } else {
            setError(result.error);
        }
    };

    const handleConfigureGamePoints = (game) => {
        setSelectedGame(game);
        setPointsConfigDialog(true);
    };

    const handleSaveGamePoints = async (config) => {
        if (!selectedGame) return;

        const result = await gulagService.updateGamePointsConfig(
            selectedGame.id,
            config.game_type,
            config.points_config,
            config.match_limits
        );

        if (result.success) {
            setSuccess(`Points configuration for ${selectedGame.name} updated successfully!`);
            setPointsConfigDialog(false);
            setSelectedGame(null);
            loadData();
        } else {
            setError(result.error);
        }
    };

    const getGameTypeLabel = (gameType) => {
        const labels = {
            session: 'Session',
            challenge: 'Challenge',
            time_based: 'Time Based'
        };
        return labels[gameType] || 'Challenge';
    };

    const getGameTypeColor = (gameType) => {
        const colors = {
            session: 'primary',
            challenge: 'success',
            time_based: 'warning'
        };
        return colors[gameType] || 'default';
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                    Gulag Management
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Configure Gulag settings, games, and point systems
                </Typography>
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}

            {/* Tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
                    <Tab label="Event Configuration" icon={<EventIcon />} iconPosition="start" />
                    <Tab label="Game Management" icon={<GameIcon />} iconPosition="start" />
                    <Tab label="Time Trials" icon={<TimerIcon />} iconPosition="start" />
                </Tabs>
            </Box>

            {/* Event Management Tab */}
            <TabPanel value={activeTab} index={0}>
                <Paper>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <EventIcon color="primary" />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Event Gulag Configuration ({events.filter(e => e.gulag_enabled).length} enabled)
                            </Typography>
                        </Box>
                    </Box>
                    {loading ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : events.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                No events found
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Event Name</strong></TableCell>
                                        <TableCell><strong>Date</strong></TableCell>
                                        <TableCell><strong>Location</strong></TableCell>
                                        <TableCell><strong>Status</strong></TableCell>
                                        <TableCell><strong>Organizer</strong></TableCell>
                                        <TableCell><strong>Gulag Enabled</strong></TableCell>
                                        <TableCell><strong>Multiplier</strong></TableCell>
                                        <TableCell align="right"><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {events.map((event) => (
                                        <TableRow key={event.id}>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {event.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {event.date ? new Date(event.date).toLocaleDateString() : 'N/A'}
                                            </TableCell>
                                            <TableCell>{event.location || 'N/A'}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={event.status || 'upcoming'}
                                                    size="small"
                                                    color={event.status === 'ongoing' ? 'success' : event.status === 'completed' ? 'default' : 'info'}
                                                />
                                            </TableCell>
                                            <TableCell>{event.organizer_name || 'N/A'}</TableCell>
                                            <TableCell>
                                                <Switch
                                                    checked={event.gulag_enabled || false}
                                                    onChange={() => handleToggleEventGulag(event.id, event.gulag_enabled)}
                                                    color="primary"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {event.gulag_enabled ? (
                                                    <Chip
                                                        label={`${event.gulag_points_multiplier}x`}
                                                        size="small"
                                                        color="primary"
                                                    />
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">
                                                        N/A
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    startIcon={<EditIcon />}
                                                    onClick={() => handleEditEvent(event)}
                                                    disabled={!event.gulag_enabled}
                                                >
                                                    Configure
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            </TabPanel>

            {/* Game Management Tab */}
            <TabPanel value={activeTab} index={1}>
                <Paper>
                    <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <GameIcon color="primary" />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    Game Points Configuration ({games.length} games)
                                </Typography>
                            </Box>
                            <Alert severity="info" sx={{ py: 0.5 }}>
                                Configure how points are awarded for each game type
                            </Alert>
                        </Box>
                    </Box>
                    {loading ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : games.length === 0 ? (
                        <Box sx={{ p: 4, textAlign: 'center' }}>
                            <Typography variant="body1" color="text.secondary">
                                No games found. Create games to configure point systems.
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell><strong>Game Name</strong></TableCell>
                                        <TableCell><strong>Category</strong></TableCell>
                                        <TableCell><strong>Type</strong></TableCell>
                                        <TableCell><strong>Max Players</strong></TableCell>
                                        <TableCell><strong>Team Based</strong></TableCell>
                                        <TableCell><strong>Match Limit</strong></TableCell>
                                        <TableCell align="right"><strong>Actions</strong></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {games.map((game) => (
                                        <TableRow key={game.id}>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {game.name}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={game.category}
                                                    size="small"
                                                    color={game.category === 'competitive' ? 'error' : game.category === 'irl' ? 'warning' : 'success'}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={getGameTypeLabel(game.game_type)}
                                                    size="small"
                                                    color={getGameTypeColor(game.game_type)}
                                                    variant="outlined"
                                                />
                                            </TableCell>
                                            <TableCell>{game.max_players_per_side}</TableCell>
                                            <TableCell>
                                                {game.team_based ? (
                                                    <Chip label="Yes" size="small" color="primary" variant="outlined" />
                                                ) : (
                                                    <Chip label="No" size="small" variant="outlined" />
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {game.match_limits?.max_matches_per_opponent || 5} per opponent
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    startIcon={<SettingsIcon />}
                                                    onClick={() => handleConfigureGamePoints(game)}
                                                >
                                                    Configure Points
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </Paper>
            </TabPanel>

            {/* Time Trials Tab */}
            <TabPanel value={activeTab} index={2}>
                <TimeTrialManagement />
            </TabPanel>

            {/* Event Settings Dialog */}
            <Dialog open={editEventDialog} onClose={() => setEditEventDialog(false)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <EventIcon color="primary" />
                        Configure Gulag Settings
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedEvent && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                {selectedEvent.name}
                            </Typography>
                            <Box sx={{ mt: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography variant="body1">
                                        Enable Gulag for this event
                                    </Typography>
                                    <Switch
                                        checked={eventSettings.gulag_enabled}
                                        onChange={(e) => setEventSettings({
                                            ...eventSettings,
                                            gulag_enabled: e.target.checked
                                        })}
                                        color="primary"
                                    />
                                </Box>
                                <TextField
                                    fullWidth
                                    label="Points Multiplier"
                                    type="number"
                                    value={eventSettings.gulag_points_multiplier}
                                    onChange={(e) => setEventSettings({
                                        ...eventSettings,
                                        gulag_points_multiplier: parseFloat(e.target.value) || 1.0
                                    })}
                                    inputProps={{ min: 0.1, max: 10.0, step: 0.1 }}
                                    helperText="Multiplier for points earned in this event's Gulag matches"
                                    disabled={!eventSettings.gulag_enabled}
                                />
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditEventDialog(false)}>Cancel</Button>
                    <Button onClick={handleSaveEventSettings} variant="contained" color="primary">
                        Save Settings
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Game Points Configuration Dialog */}
            <GamePointsConfigDialog
                open={pointsConfigDialog}
                onClose={() => {
                    setPointsConfigDialog(false);
                    setSelectedGame(null);
                }}
                game={selectedGame}
                onSave={handleSaveGamePoints}
            />
        </Box>
    );
}

export default GulagManagement;

